// plan-modusign-style-acceptance §Phase2 — 새 서명 생성 마법사 모달.
//
// Round 2 Phase G (#5) — 2단계: 양식 선택 → 로봇/고객 선택. 기존 "3.서명자"
// step 을 제거했다. 서명자는 백엔드(create)가 템플릿 signerRolesJson 으로 슬롯을
// 자동 합성하고(이름/전화 공란), 이름은 IN_PERSON 대면 서명 시점에 입력된다.
// 서명 방식은 동시(PARALLEL) 고정. "발송" → "생성" 으로 명칭 통일.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as pdfjsLib from 'pdfjs-dist';
import type { SignatureField, SignatureTemplateSummaryDto } from '@bhops/shared';
import { SignatureSignOrder as SignOrderEnum } from '@bhops/shared';
import { signatureTemplatesApi } from '@/api/signatureTemplates';
import { signatureDocumentsApi } from '@/api/signatureDocuments';
import { apiClient, extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { PdfCanvasViewer } from './PdfCanvasViewer';
import { SignDocumentPrefillPanel } from './SignDocumentPrefillPanel';

// 개선계획 §5-4 (결정 ⑥-1A) — 첨부 규격. 서버(attachment-spec.ts)와 같은 값.
// 여기서 먼저 막아 규격 밖 파일이 S3 에 올라간 뒤 발송에서 거절되는 일을 없앤다.
const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const MAX_ATTACHMENT_PAGES = 30;
// 합본 미리보기 축소 렌더 크기(디자이너 794x1122 슬롯의 절반).
const PREVIEW_W = 397;
const PREVIEW_H = 561;

/** 첨부 후보 PDF 의 페이지 수. PDF 로 열리지 않으면 0. */
async function readPdfPageCount(file: File): Promise<number> {
  try {
    const buf = await file.arrayBuffer();
    const doc = await pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
    return doc.numPages;
  } catch {
    return 0;
  }
}

// sign-console-prefill-2026-07 §3a — 생성 마법사 선택적 미리 채우기 단계용.
// 운영자가 손으로 채우는 필드 = prefillable + literal(자동 바인딩 아님).
const isEditablePrefill = (f: SignatureField): boolean =>
  (f.type === 'text' || f.type === 'date' || f.type === 'checkbox' || f.type === 'dropdown') &&
  f.binding.kind === 'literal';

// ── Lightweight types for picker ──────────────────────────────────────────
interface RobotRow {
  id: string;
  serialShort: string | null;
  modelDisplay: string;
}
interface CustomerRow {
  id: string;
  displayName: string;
}

const field: CSSProperties = { marginBottom: 14 };
const label: CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5 };
const input: CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const stepHeader: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-secondary)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  marginBottom: 16,
};

interface Props {
  onClose: () => void;
  onCreated: (id: string) => void;
  /** 현장 앱 '오늘 방문'에서 넘어온 고객사 — 고객/로봇 단계에서 미리 골라 둔다. */
  initialCustomer?: CustomerRow | null;
}

type Step = 'template' | 'attachment' | 'target' | 'prefill';

export function SignDocumentNewModal({ onClose, onCreated, initialCustomer = null }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('template');
  // §3a — 생성 후 미리 채우기 단계용. 생성된 문서 id.
  const [createdId, setCreatedId] = useState<string | null>(null);

  // Step 0 state
  const [selectedTemplate, setSelectedTemplate] = useState<SignatureTemplateSummaryDto | null>(
    null,
  );

  // ── 첨부 단계 상태 (결정 ⑥-1A) ─────────────────────────────────────────
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentPages, setAttachmentPages] = useState(0);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachmentS3Key, setAttachmentS3Key] = useState<string | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const needsAttachment = !!selectedTemplate?.allowAttachment;
  const steps: Array<{ key: Step; label: string }> = [
    { key: 'template', label: '양식 선택' },
    ...(needsAttachment ? [{ key: 'attachment' as Step, label: '첨부 · 미리보기' }] : []),
    { key: 'target', label: '고객/로봇' },
    { key: 'prefill', label: '미리 채우기' },
  ];
  const stepIdx = Math.max(
    0,
    steps.findIndex((s) => s.key === step),
  );

  // blob URL 은 파일이 바뀌거나 모달이 닫힐 때 반드시 해제(메모리 누수).
  useEffect(() => {
    if (!attachmentUrl) return;
    return () => URL.revokeObjectURL(attachmentUrl);
  }, [attachmentUrl]);

  // 첨부를 쓰지 않는 양식으로 바꾸면 고른 첨부는 버린다.
  useEffect(() => {
    if (needsAttachment) return;
    setAttachmentFile(null);
    setAttachmentPages(0);
    setAttachmentError(null);
    setAttachmentUrl(null);
    setAttachmentS3Key(null);
  }, [needsAttachment]);

  // 합본 미리보기용 본문 PDF — 양식 상세의 presigned URL.
  const templateDetailQ = useQuery({
    queryKey: ['signature-template-detail', selectedTemplate?.id],
    queryFn: () => signatureTemplatesApi.get(selectedTemplate!.id),
    enabled: step === 'attachment' && !!selectedTemplate,
  });

  // Step 1 state
  const [robotSearch, setRobotSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  // round3 #2① — 로봇 다중 선택(여러 대/모델 계약).
  const [selectedRobots, setSelectedRobots] = useState<RobotRow[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerRow | null>(initialCustomer);

  // ── Queries ────────────────────────────────────────────────────────────
  const templatesQ = useQuery({
    queryKey: ['signature-templates-for-new'],
    queryFn: () => signatureTemplatesApi.list({ includeArchived: false }),
  });

  const robotsQ = useQuery({
    queryKey: ['robots-picker', robotSearch],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: Array<{ id: string; serialShort: string | null; model: { displayName: string } }>;
      }>('/robots', { params: { search: robotSearch || undefined, pageSize: 30 } });
      return (data.items ?? []).map((r) => ({
        id: r.id,
        serialShort: r.serialShort,
        modelDisplay: r.model.displayName,
      })) as RobotRow[];
    },
    enabled: step === 'target',
  });

  const customersQ = useQuery({
    queryKey: ['customers-picker', customerSearch],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: Array<{ id: string; displayName: string }> }>(
        '/customers',
        { params: { search: customerSearch || undefined, pageSize: 30 } },
      );
      return (data.items ?? []).map((c) => ({
        id: c.id,
        displayName: c.displayName,
      })) as CustomerRow[];
    },
    enabled: step === 'target',
  });

  // round6 §1-9 (D12) — 고객사 선택 시 그 고객사에 출고 배정(예약)된 로봇을
  // 자동 선택한다. 로봇 피커는 기본 접힘(요약 표시) — [수정]으로 펼치면 기존
  // 검색 피커로 추가/제거(필드팀 수정 케이스). 배정 0대면 피커를 자동으로 연다.
  const [robotEditOpen, setRobotEditOpen] = useState(false);
  const reservedQ = useQuery({
    queryKey: ['robots-reserved-for', selectedCustomer?.id],
    enabled: step === 'target' && !!selectedCustomer,
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: Array<{ id: string; serialShort: string | null; model: { displayName: string } }>;
      }>('/robots', { params: { reservedForCustomerId: selectedCustomer!.id, pageSize: 100 } });
      return (data.items ?? []).map((r) => ({
        id: r.id,
        serialShort: r.serialShort,
        modelDisplay: r.model.displayName,
      })) as RobotRow[];
    },
  });
  // 고객사가 바뀐 첫 응답에서만 자동 채움 — 이후 수동 추가/제거를 덮지 않는다.
  const autoFilledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedCustomer || !reservedQ.data) return;
    if (autoFilledFor.current === selectedCustomer.id) return;
    autoFilledFor.current = selectedCustomer.id;
    setSelectedRobots(reservedQ.data);
    setRobotEditOpen(reservedQ.data.length === 0);
  }, [selectedCustomer, reservedQ.data]);

  // ── Mutation ───────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: () => {
      // 2026-09-23 — 로봇은 골라도 되고 안 골라도 된다(나중에 서명 관리의 '로봇 수정').
      if (!selectedTemplate || !selectedCustomer) {
        throw new Error('필수 항목이 누락되었습니다.');
      }
      if (selectedTemplate.allowAttachment && !attachmentS3Key) {
        throw new Error('첨부 문서를 먼저 선택해 주세요.');
      }
      return signatureDocumentsApi.create({
        templateId: selectedTemplate.id,
        // 결정 ⑥-1A — 첨부를 쓰는 양식은 첨부 없이 발송할 수 없다(서버도 400).
        ...(attachmentS3Key ? { attachmentS3Key } : {}),
        robotIds: selectedRobots.map((r) => r.id),
        customerId: selectedCustomer.id,
        // Round 2 Phase G (#5) — 서명자는 서버가 템플릿 역할로 자동 합성.
        // 동시(PARALLEL) 서명 고정, 이름은 대면 서명 시점에 입력.
        signOrder: SignOrderEnum.PARALLEL,
        signatories: [],
      });
    },
    onSuccess: ({ id }) => {
      toast.success('서명 문서가 생성되었습니다.');
      // 목록 최신화 — 이제 생성 성공 시점에 무효화한다(이전엔 onCreated 에서만 했음).
      // step2(미리 채우기)를 ×/Esc 로 닫아도 새 문서가 목록에 바로 보이도록.
      qc.invalidateQueries({ queryKey: ['signature-documents'] });
      // §3a — 바로 닫지 않고 선택적 '미리 채우기' 단계로. 채울 필드가 없으면
      // 아래 step2 effect 가 즉시 onCreated 로 넘긴다.
      setCreatedId(id);
      setStep('prefill');
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // §3a — step2 진입 시 생성된 문서 상세를 불러와 미리 채우기 패널에 사용.
  const createdDocQ = useQuery({
    queryKey: ['signature-document', createdId],
    queryFn: () => signatureDocumentsApi.get(createdId!),
    enabled: step === 'prefill' && !!createdId,
  });
  const createdEditableFields = (createdDocQ.data?.fieldsJson ?? []).filter(isEditablePrefill);
  // 채울(직접 입력) 필드가 없으면 미리 채우기 단계를 건너뛰고 바로 상세로.
  useEffect(() => {
    if (step === 'prefill' && createdId && createdDocQ.data && createdEditableFields.length === 0) {
      onCreated(createdId);
    }
  }, [step, createdId, createdDocQ.data, createdEditableFields.length, onCreated]);

  // ── Handlers ───────────────────────────────────────────────────────────
  function handleTemplateNext() {
    if (!selectedTemplate) return;
    setStep(needsAttachment ? 'attachment' : 'target');
  }

  /**
   * 결정 ⑥-1A — 첨부 파일 선택. 규격(PDF · 20MB · 30페이지)을 이 자리에서
   * 판정해 규격 밖 파일은 업로드 자체를 막는다(서버가 최종 판정을 다시 한다).
   */
  async function handleAttachmentPick(file: File) {
    setAttachmentS3Key(null);
    setAttachmentFile(null);
    setAttachmentPages(0);
    setAttachmentUrl(null);
    if (file.type && file.type !== 'application/pdf') {
      setAttachmentError('PDF 파일만 첨부할 수 있습니다.');
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError(
        `첨부 파일은 ${MAX_ATTACHMENT_BYTES / 1024 / 1024}MB까지 올릴 수 있습니다. 이 파일은 ${(file.size / 1024 / 1024).toFixed(1)}MB입니다.`,
      );
      return;
    }
    const pages = await readPdfPageCount(file);
    if (pages === 0) {
      setAttachmentError('이 파일은 PDF 로 열 수 없습니다. 다른 파일을 선택해 주세요.');
      return;
    }
    if (pages > MAX_ATTACHMENT_PAGES) {
      setAttachmentError(
        `첨부 파일은 ${MAX_ATTACHMENT_PAGES}페이지까지 올릴 수 있습니다. 이 파일은 ${pages}페이지입니다.`,
      );
      return;
    }
    setAttachmentError(null);
    setAttachmentPages(pages);
    setAttachmentFile(file);
    setAttachmentUrl(URL.createObjectURL(file));
  }

  async function handleAttachmentNext() {
    if (!attachmentFile) return;
    if (attachmentS3Key) {
      setStep('target');
      return;
    }
    setUploadingAttachment(true);
    try {
      const { uploadUrl, s3Key, headers } = await signatureDocumentsApi.requestAttachmentUploadUrl(
        attachmentFile.name,
        attachmentFile.size,
      );
      await signatureDocumentsApi.uploadAttachment(uploadUrl, attachmentFile, headers);
      setAttachmentS3Key(s3Key);
      setStep('target');
    } catch (err) {
      const message = extractApiError(err);
      toast.error(message);
    } finally {
      setUploadingAttachment(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Modal
      open
      title="새 서명 생성"
      onClose={onClose}
      width={step === 'prefill' || step === 'attachment' ? 920 : 560}
    >
      {/* FB §7 — 고정 width:460 → 모달 폭(560)에 맞춰 가변(100%). */}
      <div style={{ width: '100%', padding: '4px 0' }}>
        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {/* 첨부를 쓰는 양식일 때만 '첨부 · 미리보기' 단계가 끼어든다(결정 ⑥-1A). */}
          {steps.map((s, i) => (
            <span
              key={s.key}
              style={{
                fontSize: 11,
                padding: '3px 10px',
                borderRadius: 'var(--r-pill)',
                background:
                  stepIdx === i
                    ? 'var(--brand)'
                    : stepIdx > i
                      ? 'var(--bg-success-subtle)'
                      : 'var(--bg-surface)',
                color:
                  stepIdx === i
                    ? 'var(--fg-inverse)'
                    : stepIdx > i
                      ? 'var(--fg-on-success-tint)'
                      : 'var(--text-secondary)',
                border: '1px solid ' + (stepIdx === i ? 'transparent' : 'var(--border-default)'),
                fontWeight: stepIdx === i ? 700 : 400,
              }}
            >
              {i + 1}. {s.label}
            </span>
          ))}
        </div>

        {/* ── Step 0: Select template ── */}
        {step === 'template' && (
          <>
            <div style={stepHeader}>서명 양식 선택</div>
            {templatesQ.isLoading && (
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>불러오는 중…</p>
            )}
            <div
              style={{
                maxHeight: 320,
                overflowY: 'auto',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-card)',
              }}
            >
              {(templatesQ.data ?? []).map((t) => {
                // round3 #2② — PDF 미첨부 / 서명역할 0 인 템플릿은 create 가 400 으로
                // 거부(서명 생성 실패의 근본 원인). 선택 불가 처리 + 사유 표기.
                const blockers: string[] = [];
                if (!t.hasPdf) blockers.push('PDF 없음');
                if (t.signerRoleCount === 0) blockers.push('서명 역할 없음');
                const usable = blockers.length === 0;
                return (
                  <div
                    key={t.id}
                    onClick={() => {
                      if (usable) setSelectedTemplate(t);
                    }}
                    aria-disabled={!usable}
                    title={
                      usable ? undefined : `이 양식으로는 보낼 수 없습니다: ${blockers.join(', ')}`
                    }
                    style={{
                      padding: '10px 14px',
                      cursor: usable ? 'pointer' : 'not-allowed',
                      opacity: usable ? 1 : 0.55,
                      background:
                        selectedTemplate?.id === t.id ? 'var(--bg-info-subtle)' : 'transparent',
                      borderBottom: '1px solid var(--border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background:
                          selectedTemplate?.id === t.id ? 'var(--brand)' : 'var(--border-default)',
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        v{t.version} · {t.pageCount}p · {t.documentType}
                        {!usable && (
                          <span style={{ color: 'var(--fg-danger)' }}>
                            {' '}
                            · 발송 불가: {blockers.join(', ')}
                          </span>
                        )}
                        {/* round5 #5i — 막다른 길 해소: 사유만 보여주지 않고
                            해결 경로(디자이너 — PDF 업로드/역할 관리)로 직행. */}
                        {!usable && (
                          <Link
                            to={`/field/sign/templates/${t.id}/design`}
                            style={{ marginLeft: 6, color: 'var(--fg-brand)' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            디자이너에서 열기
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="ghost" onClick={onClose}>
                취소
              </Button>
              <Button onClick={handleTemplateNext} disabled={!selectedTemplate}>
                다음
              </Button>
            </div>
          </>
        )}

        {/* ── 첨부 · 합본 미리보기 (결정 ⑥-1A) ── */}
        {step === 'attachment' && (
          <>
            <div style={stepHeader}>첨부 문서 선택과 합본 미리보기</div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
              이 양식은 발송할 때 첨부 문서를 함께 보냅니다. 첨부는 양식 뒤에 그대로 붙고, 서명이
              끝나면 마지막에 인증 페이지가 추가됩니다. 첨부는 {MAX_ATTACHMENT_BYTES / 1024 / 1024}
              MB,
              {MAX_ATTACHMENT_PAGES}페이지 이하의 PDF만 올릴 수 있습니다.
            </p>

            <div style={field}>
              <label style={label}>첨부 PDF *</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void handleAttachmentPick(f);
                }}
                style={{ fontSize: 13 }}
              />
              {attachmentError && (
                <p role="alert" style={{ color: 'var(--color-error)', fontSize: 12, marginTop: 6 }}>
                  {attachmentError}
                </p>
              )}
              {attachmentFile && !attachmentError && (
                <p style={{ fontSize: 12, color: 'var(--fg-brand)', marginTop: 6 }}>
                  {attachmentFile.name} · {attachmentPages}페이지 ·{' '}
                  {(attachmentFile.size / 1024 / 1024).toFixed(1)}MB
                </p>
              )}
            </div>

            {/* 합본 미리보기 — 본문 페이지에 이어 첨부 페이지. 인증 페이지는
                서명 완료 시점에 서버가 만든다(여기엔 없음). */}
            <div
              style={{
                maxHeight: 420,
                overflowY: 'auto',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-card)',
                padding: 12,
                background: 'var(--bg-sunken)',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                1. 본문 ({selectedTemplate?.pageCount ?? 0}페이지)
              </div>
              <div
                style={{
                  position: 'relative',
                  width: PREVIEW_W,
                  height: PREVIEW_H * (selectedTemplate?.pageCount ?? 1),
                  margin: '0 auto 16px',
                }}
              >
                {templateDetailQ.data?.pdfDownloadUrl && (
                  <PdfCanvasViewer
                    url={templateDetailQ.data.pdfDownloadUrl}
                    pageWidth={PREVIEW_W}
                    pageHeight={PREVIEW_H}
                  />
                )}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                2. 첨부 ({attachmentPages}페이지)
              </div>
              {attachmentUrl ? (
                <div
                  style={{
                    position: 'relative',
                    width: PREVIEW_W,
                    height: PREVIEW_H * Math.max(1, attachmentPages),
                    margin: '0 auto',
                  }}
                >
                  <PdfCanvasViewer
                    url={attachmentUrl}
                    pageWidth={PREVIEW_W}
                    pageHeight={PREVIEW_H}
                  />
                </div>
              ) : (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
                  첨부 파일을 선택하면 여기에 이어서 보입니다.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button
                variant="ghost"
                onClick={() => setStep('template')}
                disabled={uploadingAttachment}
              >
                이전
              </Button>
              <Button
                onClick={() => void handleAttachmentNext()}
                disabled={!attachmentFile || !!attachmentError || uploadingAttachment}
              >
                {uploadingAttachment ? '올리는 중…' : '다음'}
              </Button>
            </div>
          </>
        )}

        {/* ── Step 1: 고객사 먼저 → 배정 로봇 자동 선택 (round6 §1-9 / D12) ── */}
        {step === 'target' && (
          <>
            <div style={stepHeader}>고객사와 로봇 선택</div>

            <div style={field}>
              <label style={label}>고객사 *</label>
              <input
                style={input}
                placeholder="고객사명 검색…"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
              />
              <div
                style={{
                  maxHeight: 160,
                  overflowY: 'auto',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--r-card)',
                  marginTop: 4,
                }}
              >
                {(customersQ.data ?? []).map((c) => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCustomer(c)}
                    style={{
                      padding: '7px 12px',
                      cursor: 'pointer',
                      fontSize: 13,
                      background:
                        selectedCustomer?.id === c.id ? 'var(--bg-info-subtle)' : 'transparent',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                  >
                    {c.displayName}
                  </div>
                ))}
                {customersQ.isLoading && (
                  <p style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    불러오는 중…
                  </p>
                )}
              </div>
              {selectedCustomer && (
                <div style={{ fontSize: 12, color: 'var(--fg-brand)', marginTop: 4 }}>
                  선택됨: {selectedCustomer.displayName}
                </div>
              )}
            </div>

            <div style={field}>
              <label style={label}>로봇 (선택, 여러 대 가능)</label>
              {!selectedCustomer && (
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 6px' }}>
                  고객사를 먼저 선택하면 출고 배정된 로봇이 자동 선택됩니다.
                </p>
              )}
              {selectedCustomer && !robotEditOpen ? (
                // 요약 모드 — 배정 로봇 자동 선택 결과 + [수정].
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '8px 12px',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--r-card)',
                    background: 'var(--bg-sunken)',
                    fontSize: 12,
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    {reservedQ.isLoading
                      ? '배정 로봇 조회 중…'
                      : selectedRobots.length > 0
                        ? `배정된 로봇 ${selectedRobots.length}대를 자동으로 골랐습니다: ${selectedRobots
                            .map((r) => r.serialShort ?? r.id)
                            .join(', ')}`
                        : '이 고객사에 배정된 로봇이 없습니다. 비워 두고 만들어도 되고, [수정]에서 직접 고를 수도 있습니다.'}
                  </span>
                  <Button variant="ghost" onClick={() => setRobotEditOpen(true)}>
                    수정
                  </Button>
                </div>
              ) : (
                <>
                  <input
                    style={input}
                    placeholder="시리얼 번호 검색…"
                    value={robotSearch}
                    onChange={(e) => setRobotSearch(e.target.value)}
                  />
                  <div
                    style={{
                      maxHeight: 160,
                      overflowY: 'auto',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--r-card)',
                      marginTop: 4,
                    }}
                  >
                    {(robotsQ.data ?? []).map((r) => {
                      // round3 #2① — 클릭 토글 다중 선택.
                      const checked = selectedRobots.some((x) => x.id === r.id);
                      return (
                        <div
                          key={r.id}
                          onClick={() =>
                            setSelectedRobots((prev) =>
                              checked ? prev.filter((x) => x.id !== r.id) : [...prev, r],
                            )
                          }
                          style={{
                            padding: '7px 12px',
                            cursor: 'pointer',
                            fontSize: 13,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            background: checked ? 'var(--bg-info-subtle)' : 'transparent',
                            borderBottom: '1px solid var(--border-subtle)',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            style={{ pointerEvents: 'none' }}
                          />
                          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                            {r.serialShort ?? '–'}
                          </span>
                          <span style={{ color: 'var(--text-secondary)' }}>{r.modelDisplay}</span>
                        </div>
                      );
                    })}
                    {robotsQ.isLoading && (
                      <p
                        style={{
                          padding: '8px 12px',
                          fontSize: 12,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        불러오는 중…
                      </p>
                    )}
                  </div>
                  {selectedRobots.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--fg-brand)', marginTop: 4 }}>
                      {selectedRobots.length}대 선택:{' '}
                      {selectedRobots.map((r) => r.serialShort ?? r.id).join(', ')}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* 서명자는 양식의 서명 역할로 자동 생성되며, 이름은 대면 서명 시 입력합니다. */}
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
              서명자는 선택한 양식의 서명 역할로 자동 생성되며, 이름은 대면 서명 시점에 입력합니다.
            </p>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <Button
                variant="ghost"
                onClick={() => setStep(needsAttachment ? 'attachment' : 'template')}
                disabled={mutation.isPending}
              >
                이전
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                disabled={!selectedCustomer || mutation.isPending}
              >
                {mutation.isPending ? '생성 중…' : '생성'}
              </Button>
            </div>
          </>
        )}

        {/* ── Step 2: 선택적 미리 채우기 (§3a) — 생성된 문서에 바로 채운다. ── */}
        {step === 'prefill' && createdId && (
          <>
            <div style={stepHeader}>미리 채우기 (선택)</div>
            {/* 조회 실패를 먼저 판정한다 — data 가 undefined 라 editableFields.length===0 이
                되어 로딩 분기에 갇히면 안 되므로(무한 '불러오는 중' 방지). '상세로 이동'으로
                탈출 가능. */}
            {createdDocQ.isError ? (
              <div style={{ padding: '8px 0' }}>
                <p style={{ color: 'var(--color-error)', fontSize: 13 }}>
                  {extractApiError(createdDocQ.error)}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <Button onClick={() => onCreated(createdId)}>상세로 이동</Button>
                </div>
              </div>
            ) : createdDocQ.isLoading || createdEditableFields.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, padding: '8px 0' }}>
                문서가 생성되었습니다. 불러오는 중…
              </p>
            ) : (
              <>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                  지금 계약서 위에서 아는 값을 미리 채워 두면 서명 링크에 반영됩니다. 나중에 문서
                  상세 화면에서 채워도 됩니다.
                </p>
                {/* 패널의 '닫기' = 저장 없이 상세로(나중에 채우기), '미리 채우기 저장' =
                    저장 후 상세로. 둘 다 onClose(→onCreated)로 마법사를 마친다. */}
                <SignDocumentPrefillPanel
                  documentId={createdId}
                  fields={createdDocQ.data!.fieldsJson}
                  pageCount={createdDocQ.data!.pageCount}
                  fallbackPdfUrl={createdDocQ.data!.pdfDownloadUrl}
                  initialValues={createdDocQ.data!.prefilledValues ?? {}}
                  documentName={createdDocQ.data!.displayName}
                  onClose={() => onCreated(createdId)}
                />
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
